import React, { useState } from 'react';

const BusinessIncentiveIncome = () => {
  const [searchTerm, setSearchTerm] = useState({
    dateFrom: '',
    dateTo: '',
  });

  // Sample data - replace with your actual data
  const incomeData = [
    {
      id: 1,
      transactionDate: '15/06/2023',
      incomeFromId: 'RV1582376',
      incomeLevel: 'Level 1',
      incomeAmount: '₹5,000.00',
      deductTDS: '₹250.00',
      deductAdmin: '₹100.00',
      payableAmt: '₹4,650.00',
      genDate: '16/06/2023',
    },
    {
      id: 2,
      transactionDate: '20/06/2023',
      incomeFromId: 'RV1521968',
      incomeLevel: 'Level 2',
      incomeAmount: '₹3,200.00',
      deductTDS: '₹160.00',
      deductAdmin: '₹80.00',
      payableAmt: '₹2,960.00',
      genDate: '21/06/2023',
    },
    // Add more sample data as needed
  ];

  const [filteredData, setFilteredData] = useState(incomeData);

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchTerm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    // Filter logic based on date range
    const filtered = incomeData.filter((item) => {
      const itemDate = new Date(item.transactionDate.split('/').reverse().join('-'));
      const fromDate = searchTerm.dateFrom ? new Date(searchTerm.dateFrom) : null;
      const toDate = searchTerm.dateTo ? new Date(searchTerm.dateTo) : null;

      return (
        (!fromDate || itemDate >= fromDate) &&
        (!toDate || itemDate <= toDate)
      );
    });
    setFilteredData(filtered);
  };

  // Function to export data to CSV
  const exportToCSV = () => {
    const headers = [
      'Transaction Date',
      'Income From ID',
      'Income Level',
      'Income Amount',
      'Deduct TDS',
      'Deduct Admin',
      'Payable Amount',
      'Generation Date'
    ].join(',');

    const rows = filteredData.map((item) => [
      item.transactionDate,
      item.incomeFromId,
      item.incomeLevel,
      item.incomeAmount,
      item.deductTDS,
      item.deductAdmin,
      item.payableAmt,
      item.genDate
    ].join(','));

    const csvContent = [headers, ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'business_incentive_income.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="container mx-auto p-4 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Business Incentive Income</h1>
        <p className="text-gray-600">View and track your incentive income details</p>
      </div>

      {/* Search Section */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
            <input
              type="date"
              name="dateFrom"
              value={searchTerm.dateFrom}
              onChange={handleSearchChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
            <input
              type="date"
              name="dateTo"
              value={searchTerm.dateTo}
              onChange={handleSearchChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleSearch}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition transform hover:scale-105 shadow-md w-full"
            >
              SEARCH
            </button>
          </div>
        </div>
        
        <div className="flex justify-end">
          <button 
            onClick={exportToCSV}
            className="flex items-center bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-6 rounded-lg transition transform hover:scale-105 shadow-md"
            disabled={filteredData.length === 0}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            Export CSV
          </button>
        </div>
      </div>

      {/* Results Summary */}
      <div className="mb-4 flex justify-between items-center">
        <p className="text-gray-600">
          {filteredData.length > 0 ? (
            <>
              Showing <span className="font-bold">{filteredData.length}</span> records
              {searchTerm.dateFrom || searchTerm.dateTo ? (
                <>
                  {' '}for period{' '}
                  {searchTerm.dateFrom && <span className="font-bold">{searchTerm.dateFrom}</span>}
                  {searchTerm.dateFrom && searchTerm.dateTo && ' to '}
                  {searchTerm.dateTo && <span className="font-bold">{searchTerm.dateTo}</span>}
                </>
              ) : null}
            </>
          ) : (
            'No records found matching your criteria'
          )}
        </p>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Income From ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Income Level</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Income Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deduct TDS</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deduct Admin</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payable Amt.</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gen. Date</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData.length > 0 ? (
                filteredData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.transactionDate}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 font-medium">{item.incomeFromId}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.incomeLevel}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{item.incomeAmount}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-500">{item.deductTDS}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-500">{item.deductAdmin}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">{item.payableAmt}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.genDate}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-sm text-gray-500">
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination (optional) */}
      {filteredData.length > 0 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="flex-1 flex justify-between sm:hidden">
            <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
              Previous
            </button>
            <button className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">1</span> to <span className="font-medium">{filteredData.length}</span> of{' '}
                <span className="font-medium">{filteredData.length}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                  <span className="sr-only">Previous</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                <button aria-current="page" className="z-10 bg-blue-50 border-blue-500 text-blue-600 relative inline-flex items-center px-4 py-2 border text-sm font-medium">
                  1
                </button>
                <button className="bg-white border-gray-300 text-gray-500 hover:bg-gray-50 relative inline-flex items-center px-4 py-2 border text-sm font-medium">
                  2
                </button>
                <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                  <span className="sr-only">Next</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessIncentiveIncome;